import { z } from 'zod';
import { Currency, SourceSystem } from '../enums.js';
import { IsoDate, IsoDateTime, UuidString } from '../primitives.js';

/**
 * S14-BE.1 — Counterparty (единая сущность для поставщиков/клиентов/прочих).
 * Discriminator `kind`. БИН/ИИН РК обязателен для customer + supplier (ЭСФ).
 */

export const CounterpartyKind = z.enum(['customer', 'supplier', 'employee', 'other']);
export type CounterpartyKind = z.infer<typeof CounterpartyKind>;

export const CounterpartyTaxStatus = z.enum(['vat_payer', 'simplified', 'patent', 'individual']);
export type CounterpartyTaxStatus = z.infer<typeof CounterpartyTaxStatus>;

export const CounterpartyLegalForm = z.enum(['llp', 'jsc', 'sole_prop', 'individual', 'foreign']);
export type CounterpartyLegalForm = z.infer<typeof CounterpartyLegalForm>;

// B6 invariant — entity-уровень response-схемы forward-compat (см. zod-strict.test.ts).
export const CounterpartyBankDetails = z.object({
  bik: z.string(),
  iban: z.string(),
  bank_name: z.string(),
});
export type CounterpartyBankDetails = z.infer<typeof CounterpartyBankDetails>;

/** РК БИН/ИИН — ровно 12 цифр. */
const BinIin = z.string().regex(/^\d{12}$/, 'bin_iin_must_be_12_digits');

export const Counterparty = z.object({
  id: UuidString,
  // integrations B7 — entity-scoped (приватный) ИЛИ holding-scoped (общий). Ровно одно.
  entity_id: UuidString.nullable(),
  holding_id: UuidString.nullable().optional(),
  needs_review: z.boolean().optional(),
  kind: CounterpartyKind,
  name_ru: z.string().min(1).max(255),
  name_kk: z.string().nullable(),
  name_en: z.string().nullable(),
  /** BE-PROJ-2 — Short synonym для list views (e.g. «Павлодарский НПЗ» вместо
   * «ТОО Павлодарский нефтехимический завод»). Optional; FE fallback на
   * truncate(name_ru, 40) если null. Max 40 chars. */
  short_name: z.string().min(1).max(40).nullable().optional(),
  legal_form: CounterpartyLegalForm.nullable(),
  bin_iin: BinIin.nullable(),
  tax_status: CounterpartyTaxStatus,
  contract_number: z.string().nullable(),
  contract_date: IsoDate.nullable(),
  payment_terms_days: z.number().int().min(0).max(365).default(0),
  default_currency: Currency,
  bank_details: CounterpartyBankDetails.nullable(),
  is_active: z.boolean().default(true),
  onec_ref: z.string().nullable(),
  source_system: SourceSystem,
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
// S-FIX-3 hotfix: removed refine `bin_iin_required_for_customer_supplier`.
// Prod has customer/supplier counterparties without bin_iin (legacy seed data).
// Write-time validation should live in a separate CounterpartyCreateInput
// schema if needed — entity schema must accept current prod shape on read.
export type Counterparty = z.infer<typeof Counterparty>;
